import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Collapse,
  Group,
  Skeleton,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconChevronDown, IconCopy, IconPlus, IconTrash } from '@tabler/icons-react';
import { usePackage } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import type { LightTemplate } from './api';
import classes from './TemplateGroup.module.css';
import { TemplateRow } from './TemplateRow';

type TemplateGroupProps = {
  title: string;
  templates: LightTemplate[];
  onClickNew: () => void;
  isLoading?: boolean;
};

export const TemplateGroup = ({
  title,
  templates,
  onClickNew,
  isLoading = false,
}: TemplateGroupProps) => {
  const { t } = useTranslation();
  const [isOpen, { toggle }] = useDisclosure(true);
  const canCreateTemplate = usePackage().can_create_template;

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="center">
        <Group gap="xs" align="center" onClick={toggle} style={{ cursor: 'pointer' }}>
          <IconChevronDown
            size={16}
            stroke={1.5}
            style={{
              transition: 'transform 150ms ease',
              transform: isOpen ? undefined : 'rotate(-90deg)',
            }}
          />
          <Badge variant="light" radius="sm" style={{ userSelect: 'none' }}>
            {templates.length}
          </Badge>
          <Text fw={600} style={{ userSelect: 'none' }}>
            {title}
          </Text>
        </Group>
        <Tooltip label={t(i18nKeys.NOT_INCLUDED)} disabled={canCreateTemplate}>
          <Button
            variant="default"
            size="sm"
            leftSection={<IconPlus size={16} stroke={1.5} />}
            data-disabled={!canCreateTemplate || undefined}
            onClick={canCreateTemplate ? onClickNew : undefined}
          >
            {t(i18nKeys.NEW_TEMPLATE)}
          </Button>
        </Tooltip>
      </Group>
      <Collapse expanded={isOpen}>
        {(isLoading || templates.length > 0) && (
          <Card radius={8} p={0}>
            {isLoading
              ? [0, 1, 2].map(index => <TemplateRowSkeleton key={index} />)
              : templates.map(template => <TemplateRow key={template.id} template={template} />)}
          </Card>
        )}
      </Collapse>
    </Stack>
  );
};

const TemplateRowSkeleton = () => {
  const { t } = useTranslation();

  return (
    <Group className={classes.skeletonRow} justify="space-between" wrap="nowrap" px="md" py="sm">
      <Skeleton height={20} width={280} />
      <Group gap="lg" wrap="nowrap">
        {SKELETON_PILL_WIDTHS.map(width => (
          <Skeleton key={width} height={20} width={width} />
        ))}
        <Skeleton height={20} width={140} />
        <Group gap="xs" wrap="nowrap">
          <ActionIcon variant="light" aria-label={t(i18nKeys.DUPLICATE)} disabled>
            <IconCopy size={18} stroke={1.5} />
          </ActionIcon>
          <ActionIcon variant="light" color="red" aria-label={t(i18nKeys.DELETE)} disabled>
            <IconTrash size={18} stroke={1.5} />
          </ActionIcon>
        </Group>
      </Group>
    </Group>
  );
};

const SKELETON_PILL_WIDTHS = [96, 72, 110];
